import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Sparkles, TrendingUp, Users, Globe, DollarSign } from 'lucide-react';

const QuickCook = () => {
  const navigate = useNavigate();

  // Die 5 QuickCook App Screenshots
  const appScreenshots = [
    {
      url: "https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/fouzmwyu_Bildschirmfoto%202026-04-17%20um%2017.07.47.png",
      alt: "QuickCook Screenshot 1"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/z1on5jk9_Bildschirmfoto%202026-04-17%20um%2017.08.51.png",
      alt: "QuickCook Screenshot 2"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/m9xpy6h5_Bildschirmfoto%202026-04-17%20um%2017.09.01.png",
      alt: "QuickCook Screenshot 3"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/zotzjpui_Bildschirmfoto%202026-04-17%20um%2017.09.46.png",
      alt: "QuickCook Screenshot 4"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/wrryllw4_Bildschirmfoto%202026-04-17%20um%2017.11.27.png",
      alt: "QuickCook Screenshot 5"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-500/5 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header mit Zurück-Button */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Button 
            onClick={() => navigate('/portfolio')}
            variant="ghost"
            className="text-primary-dark hover:text-fuchsia-600"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Zurück zum Portfolio
          </Button>
        </div>
      </header>

      {/* Überschrift Section mit weißem Hintergrund */}
      <section className="pt-32 pb-12 px-6 bg-white">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#9B1F5C' }}>
            Project Jamie / QuickCook
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 mb-8 font-medium">
            Kochapp mit Rezeptbuch, Kochen mit Jamie auf Social Media, Food Blog
          </p>
          <Button 
            size="lg"
            className="text-white font-semibold px-8 py-3 text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            style={{ backgroundColor: '#9B1F5C' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7A1849'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9B1F5C'}
          >
            Zu Jamie
          </Button>
        </div>
      </section>

      {/* Hero Image Section */}
      <section className="relative overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/ai93ls3r_Bildschirmfoto%202026-06-15%20um%2017.48.22.png"
          alt="QuickCook Hero"
          className="w-full h-auto object-cover"
        />
      </section>

      {/* App Screenshots Horizontal */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-primary-dark text-center mb-12">
            So funktioniert QuickCook
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-fuchsia-600 scrollbar-track-gray-200">
            {appScreenshots.map((screenshot, index) => (
              <div 
                key={index} 
                className="flex-shrink-0 w-36 bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              >
                <img 
                  src={screenshot.url} 
                  alt={screenshot.alt}
                  className="w-full h-auto object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Elevator Pitch */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <Card className="glass-card border-2 border-fuchsia-100">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold text-primary-dark mb-6 text-center">
                Elevator Pitch
              </h2>
              <p className="text-xl text-fuchsia-600 font-semibold mb-4 text-center">
                Fotografiere, was du hast – Jamie zaubert dein Rezept.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                QuickCook ist eine KI-gestützte Koch-App, die aus den Zutaten in deinem Kühlschrank in Sekunden passende Rezepte erstellt – inklusive Nährwertangaben, Schritt-für-Schritt-Anleitung und persönlichem Kochbuch. Verfügbar in 6 Sprachen.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Markenwelt: Jamie */}
      <section className="py-20 px-6 bg-[#9B1F5C]">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Markenwelt: Jamie, die KI-Köchin
            </h2>
          </div>
          
          <Card className="glass-card border-2 border-fuchsia-100 bg-white/95">
            <CardContent className="p-8">
              {/* Jamie Bild */}
              <div className="mb-8 flex justify-center">
                <div className="w-80 h-80 rounded-full overflow-hidden shadow-2xl ring-4 ring-fuchsia-200">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ki-governance-legal/artifacts/qa9dydid_Bildschirmfoto%202026-06-15%20um%2018.14.44.png" 
                    alt="Jamie - Die KI-Köchin"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                Jamie ist das persönliche Gesicht der App – freundlich, zugänglich und immer hilfsbereit. Über YouTube, Instagram und TikTok kocht Jamie kurze, alltagstaugliche Rezepte und sorgt so für organische Reichweite und Markenbindung außerhalb der App.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Strategy Grid: Content, Social Media, Differenzierung */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Content-Strategie */}
            <Card className="glass-card hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-fuchsia-100 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="w-8 h-8 text-fuchsia-600" />
                </div>
                <h3 className="text-2xl font-bold text-primary-dark mb-4">
                  Content-Strategie (Blog & GEO)
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Ein Blog-Bereich auf der Landing Page mit Beiträgen zu Rezepten, Ernährung, kulturellen Essgewohnheiten und Gesundheit erhöht die organische Sichtbarkeit – insbesondere im Hinblick auf Generative Engine Optimization (GEO) für KI-Suchmaschinen wie Google AI Overviews.
                </p>
              </CardContent>
            </Card>

            {/* Social Media Strategy */}
            <Card className="glass-card hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="text-2xl font-bold text-primary-dark mb-4">
                  Social Media Strategy
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Jamie kocht auf YouTube, Instagram, TikTok einfache, leckere Rezepte. Es wird ein Call-To-Action nach jedem Video eingeblendet, um die App runterzuladen aus dem App Store.
                </p>
              </CardContent>
            </Card>

            {/* Differenzierungspotenzial */}
            <Card className="glass-card hover:shadow-2xl transition-all duration-300 md:col-span-2 lg:col-span-1">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                  <Globe className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-primary-dark mb-4">
                  Differenzierungspotenzial von QuickCook
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Kein Wettbewerber hat eine durchgängige Markenpersönlichkeit ("Jamie") mit eigenem Social-Media-Ökosystem (YouTube/Instagram/TikTok), 6 Sprachen von Beginn an, und Nährwert-/Kalorienangaben als zentrales Feature kombiniert. Das ist die Lücke, die du besetzen kannst.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Finanzierung */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <Card className="glass-card border-2 border-fuchsia-100">
            <CardContent className="p-8">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-primary-dark mb-6 text-center">
                Finanzierung
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                App kostenlos zum downloaden im App Store – Finanzierung ergibt sich aus In-App-Werbung, Affiliate-Links (Lieferungsdienste) oder Markenpartnerschaften (Lebensmittel, Küchengeräte)
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-[#9B1F5C]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Mehr Projekte entdecken?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Entdecken Sie weitere spannende Projekte aus meinem Portfolio.
          </p>
          <Button 
            onClick={() => navigate('/portfolio')}
            size="lg"
            className="bg-primary-dark hover:bg-accent-green text-white font-semibold px-8 py-3 text-lg"
          >
            Zurück zum Portfolio
          </Button>
        </div>
      </section>
    </div>
  );
};

export default QuickCook;
